import { ChevronDown, X, Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export interface Option {
  key: string;
  value: string;
}

interface AutoCompleteProps {
  id: string;
  name: string;
  options: Option[];
  selectedOption: string | null;
  onChange: (selected: string) => void;
  onInputChange?: (inputValue: string) => void;
  label?: string;
  placeholder?: string;
  validationError?: boolean;
  disabled?: boolean;
  value?: string | null;
  allowCustomInput?: boolean;
  showClearButton?: boolean;
}

const AutoComplete = ({
  id,
  name,
  options,
  selectedOption,
  onChange,
  onInputChange,
  label,
  placeholder,
  validationError,
  disabled,
  value,
  allowCustomInput = true,
  showClearButton = true,
}: AutoCompleteProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(selectedOption || value || '');
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>(
    'bottom'
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setOpen(true);

    // Filter options based on input
    const filtered = options.filter((option) =>
      option.value.toLowerCase().includes(newValue.toLowerCase())
    );
    setFilteredOptions(filtered);

    // Call external input change handler if provided
    if (onInputChange) {
      onInputChange(newValue);
    }

    // Check for exact match and auto-select if found
    const exactMatch = options.find(
      (option) => option.value.toLowerCase() === newValue.toLowerCase()
    );

    if (exactMatch) {
      // Exact match found - auto-select it
      onChange(exactMatch.value);
    } else if (allowCustomInput) {
      // If allowing custom input and no exact match, call onChange
      onChange(newValue);
    }
  };

  const handleOptionSelect = (option: Option) => {
    setInputValue(option.value);
    onChange(option.value);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleClear = () => {
    setInputValue('');
    onChange('');
    setFilteredOptions(options);
    inputRef.current?.focus();
    if (onInputChange) {
      onInputChange('');
    }
  };

  const handleInputFocus = () => {
    setOpen(true);
    setFilteredOptions(options);
  };

  const handleInputBlur = () => {
    // Delay closing to allow option selection
    setTimeout(() => {
      setOpen(false);
    }, 150);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      // If there's exactly one filtered option, select it
      if (filteredOptions.length === 1) {
        const singleOption = filteredOptions[0];
        handleOptionSelect(singleOption);
      } else if (filteredOptions.length === 0 && allowCustomInput) {
        // No options but custom input allowed - close dropdown and keep current value
        setOpen(false);
        inputRef.current?.blur();
      }
    } else if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const toggleDropdown = () => {
    if (disabled) return;
    setOpen(!open);
    if (!open) {
      inputRef.current?.focus();
    }
  };

  useEffect(() => {
    if (open && containerRef.current && dropdownRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = dropdownRef.current.offsetHeight ?? 0;
      const spaceBelow = window.innerHeight - (containerRect?.bottom ?? 0);
      const spaceAbove = containerRect?.top ?? 0;

      if (spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight) {
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
    }
  }, [open, filteredOptions]);

  // Update input value when selectedOption or value props change
  useEffect(() => {
    const newValue = selectedOption || value || '';
    if (newValue !== inputValue) {
      setInputValue(newValue);
    }
  }, [selectedOption, value]);

  // Handle browser autofill - check for changes in the actual DOM input value
  useEffect(() => {
    const checkForAutofill = () => {
      if (inputRef.current) {
        const domValue = inputRef.current.value;
        if (domValue !== inputValue && domValue) {
          // Browser autofilled a value
          setInputValue(domValue);

          // Check if autofilled value matches an option
          const matchedOption = options.find(
            (option) => option.value.toLowerCase() === domValue.toLowerCase()
          );

          if (matchedOption) {
            onChange(matchedOption.value);
          } else if (allowCustomInput) {
            onChange(domValue);
          }
        }
      }
    };

    // Check for autofill after a short delay
    const timeoutId = setTimeout(checkForAutofill, 100);

    return () => clearTimeout(timeoutId);
  }, [inputValue, options, onChange, allowCustomInput]);

  const convertStartToUppercase = (text: string) => {
    return `${text.charAt(0).toUpperCase()}${text.slice(1, text.length)}`;
  };

  const showClear = showClearButton && inputValue && !disabled;

  return (
    <>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium mb-2">
          {convertStartToUppercase(label)}
        </label>
      )}

      <div className="relative w-full" ref={containerRef}>
        <div className="relative">
          <input
            ref={inputRef}
            id={id}
            name={name}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            placeholder={convertStartToUppercase(placeholder ?? '')}
            className={`w-full py-[14px] ps-4 pe-16 border border-gray-200 bg-white rounded-lg text-sm  placeholder-gray-500 disabled:opacity-50 disabled:pointer-events-none disabled:bg-gray-100 focus:border-blue-500 focus:ring-blue-500 ${
              open ? 'border-slate-500' : ''
            } ${validationError ? 'input-control-error' : ''}`}
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            autoComplete="off"
          />
          {/* Clear button */}
          {showClear && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute top-1/2 -translate-y-1/2 end-8 flex items-center justify-center w-6 h-6 text-gray-500 hover:text-red-600 focus:outline-hidden focus:text-red-600"
              aria-label="Clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          {/* Dropdown toggle button */}
          <button
            type="button"
            onClick={toggleDropdown}
            disabled={disabled}
            className="absolute top-1/2 end-3 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-hidden"
            aria-expanded={open}
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Backdrop */}
        {open && (
          <button
            type="button"
            onClick={() => setOpen(false)}
            tabIndex={-1}
            className="w-screen h-full fixed inset-0 z-10"
          />
        )}

        {/* Dropdown */}
        {open && (
          <ul
            ref={dropdownRef}
            className={`shadow-md max-h-60 overflow-y-auto absolute left-0 w-full bg-white border border-gray-200 rounded-lg px-1 z-20 ${
              dropdownPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'
            }`}
            role="listbox"
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={option.key}
                  className="hover:bg-slate-50 rounded-lg my-1 py-3 px-4 cursor-pointer"
                  onClick={() => handleOptionSelect(option)}
                  role="option"
                  tabIndex={0}
                >
                  <div className="flex justify-between items-center w-full">
                    <span className="text-sm ">{option.value}</span>
                    {selectedOption === option.value && (
                      <Check className="w-3.5 h-3.5 text-blue-600" />
                    )}
                  </div>
                </li>
              ))
            ) : (
              <li className="py-3 px-4 text-sm text-gray-500">
                No options found
              </li>
            )}
          </ul>
        )}
      </div>
    </>
  );
};

export default AutoComplete;
